import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useSEO } from '../hooks/useSEO'
import { Box, Card, CardContent, Button, Heading, Text, Skeleton } from '../components/ui'
import './Home.css'

interface Stats {
  modules: number
  knowledgeBase: number
  loading: boolean
}

const Home: React.FC = () => {
  // SEO Meta Tags
  useSEO({
    title: 'wojskaspecjalne.pl — szkolenia praktyczne i warsztaty dla firm',
    description: 'Szkolenia prowadzone przez instruktorów oraz warsztaty dla firm: decyzyjność pod presją, bezpieczeństwo, współpraca i odporność informacyjna. Poznaj metodykę (Kossecki, Mazur) i dołącz do społeczności.',
    keywords: 'szkolenia, warsztaty dla firm, decyzyjność, bezpieczeństwo, współpraca, odporność informacyjna, krytyczne myślenie, cybernetyka, metacybernetyka, Józef Kossecki, Marian Mazur',
  })
  
  const [stats, setStats] = useState<Stats>({
    modules: 0,
    knowledgeBase: 0,
    loading: true
  })
  
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      // Pobierz liczbę opublikowanych modułów
      const { count: modulesCount } = await supabase
        .from('modules')
        .select('*', { count: 'exact', head: true })
        .eq('published', true)

      // Pobierz liczbę opublikowanych artykułów z bazy wiedzy
      const { count: knowledgeCount } = await supabase
        .from('knowledge_base')
        .select('*', { count: 'exact', head: true })
        .eq('published', true)

      setStats({
        modules: modulesCount || 0,
        knowledgeBase: knowledgeCount || 0,
        loading: false
      })
      setIsInitialLoad(false)
      setHasError(false)
    } catch (error) {
      console.error('Błąd podczas ładowania statystyk:', error)
      // On error, set default values and show content (not empty page)
      setStats({
        modules: 0,
        knowledgeBase: 0,
        loading: false
      })
      setHasError(true)
      setIsInitialLoad(false)
    }
  }
  
  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box className="home-page premium-page-bg">
      <Box as="section" className="home-hero">
        <Box className="home-container premium-page-container">
          <Box className="home-hero-content">
            <Skeleton style={{ width: '200px', height: '16px', marginBottom: '1.5rem' }} />
            <Skeleton style={{ width: '100%', maxWidth: '600px', height: '48px', marginBottom: '1rem', marginLeft: 'auto', marginRight: 'auto' }} />
            <Skeleton style={{ width: '100%', maxWidth: '500px', height: '24px', marginBottom: '2.5rem', marginLeft: 'auto', marginRight: 'auto' }} />
            <Skeleton style={{ width: '200px', height: '48px', borderRadius: '8px', marginLeft: 'auto', marginRight: 'auto' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
  
  // Show skeleton only on initial load
  if (isInitialLoad && stats.loading) {
    return <LoadingSkeleton />
  }

  return (
    <Box className="home-page premium-page-bg">
      {/* Hero Section - Split into two domains */}
      <Box as="section" id="hero" className="home-hero">
        <Box className="home-container premium-page-container">
          <Box className="home-hero-content">
            <Text className="hero-badge">
              SZKOLENIA PRAKTYCZNE
            </Text>
            <Heading as="h1" size="9" className="home-hero-title">
              Szkolenia, które{' '}
              <Text as="span" className="home-hero-accent">budują sprawczość</Text>
            </Heading>
            <Text className="home-hero-subtitle" size="4">
              Trening z instruktorem + warsztaty dla firm. Mniej chaosu, więcej decyzji pod presją, procedur, współpracy i odporności informacyjnej.
            </Text>

            <Box className="home-hero-actions">
              <Link to="/szkolenia">
                <Button variant="solid" size="3" style={{ paddingLeft: '3rem', paddingRight: '3rem' }}>
                  Zobacz szkolenia
                </Button>
              </Link>
              <Link to="/dla-firm">
                <Button variant="surface" size="3" style={{ paddingLeft: '3rem', paddingRight: '3rem' }}>
                  Oferta dla firm
                </Button>
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Start lanes (A + D + C) */}
      <Box as="section" className="home-services" aria-labelledby="home-start-title">
        <Box className="home-container">
          <Heading as="h2" size="8" id="home-start-title" className="home-section-title-center">
            Wybierz ścieżkę startu
          </Heading>

          <Box className="premium-grid">
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Szkolenia (indywidualnie)
                </Heading>
                <Text className="home-service-text" size="3">
                  Praktyka z instruktorem: energokinetyczne i medyczne. Zobacz terminy, poziomy i wymagania.
                </Text>
                <Link to="/szkolenia" className="home-service-link">
                  Zobacz szkolenia →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Dla firm (B2B)
                </Heading>
                <Text className="home-service-text" size="3">
                  Warsztaty i treningi zespołowe: decyzyjność pod presją, bezpieczeństwo, komunikacja i procedury.
                </Text>
                <Link to="/dla-firm" className="home-service-link">
                  Oferta dla firm →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-3">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Poznaj metodykę
                </Heading>
                <Text className="home-service-text" size="3">
                  Biblioteka i materiały o cybernetyce/metacybernetyce (Kossecki, Mazur) + społeczność dyskusyjna.
                </Text>
                <Link to="/biblioteka" className="home-service-link">
                  Przeglądaj bibliotekę →
                </Link>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Kinetic Warfare Section */}
      <Box as="section" className="home-kinetic-warfare">
        <Box className="home-container">
          <Box className="home-kinetic-content">
            <Text className="home-section-tag" weight="bold" size="1">
              WALKA ENERGOKINETYCZNA
            </Text>
            <Heading as="h2" size="8" className="home-section-title">
              Szkolenia z instruktorem
            </Heading>
            <Text className="home-kinetic-text" size="4">
              Praktyczne szkolenia fizyczne prowadzone przez doświadczonych instruktorów. 
              Rozwijaj umiejętności bojowe, survivalowe i medyczne w realnych warunkach.
            </Text>

            <Box className="premium-grid" style={{ marginTop: '3rem' }}>
              <Card size="4" className="fade-in-up stagger-1">
                <CardContent>
                  <Heading as="h3" size="5" className="home-feature-title">
                    Obsługa Broni
                  </Heading>
                  <Text className="home-feature-description" size="3">
                    Profesjonalne szkolenia z obsługi broni palnej i białej. 
                    Techniki strzeleckie, bezpieczeństwo, konserwacja i taktyka bojowa.
                  </Text>
                  <Link to="/szkolenia" className="home-service-link" style={{ marginTop: '1.5rem' }}>
                    Zobacz szkolenia →
                  </Link>
                </CardContent>
              </Card>

              <Card size="4" className="fade-in-up stagger-2">
                <CardContent>
                  <Heading as="h3" size="5" className="home-feature-title">
                    Survival
                  </Heading>
                  <Text className="home-feature-description" size="3">
                    Przetrwanie w ekstremalnych warunkach. Budowa schronień, pozyskiwanie wody i pożywienia, 
                    nawigacja terenowa i techniki przetrwania w różnych środowiskach.
                  </Text>
                  <Link to="/szkolenia" className="home-service-link" style={{ marginTop: '1.5rem' }}>
                    Zobacz szkolenia →
                  </Link>
                </CardContent>
              </Card>

              <Card size="4" className="fade-in-up stagger-3">
                <CardContent>
                  <Heading as="h3" size="5" className="home-feature-title">
                    Medycyna Pola Walki
                  </Heading>
                  <Text className="home-feature-description" size="3">
                    Ratownictwo medyczne w warunkach bojowych. Pierwsza pomoc pod ostrzałem, 
                    ewakuacja rannych, opatrywanie ran i podstawy medycyny taktycznej.
                  </Text>
                  <Link to="/szkolenia" className="home-service-link" style={{ marginTop: '1.5rem' }}>
                    Zobacz szkolenia →
                  </Link>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Value Proposition */}
      <Box as="section" id="value" className="home-info-warfare">
        <Box className="home-container">
          <Box className="home-info-warfare-content">
            <Text className="home-section-tag" weight="bold" size="1">
              WALKA INFORMACYJNA
            </Text>
            <Heading as="h2" size="8" className="home-section-title">
              Działaj Zgodnie z Własnym Interesem
            </Heading>
            <Text className="home-info-warfare-text" size="4">
              Współczesna wojna odbywa się w domenie informacyjnej, gdzie celem jest odebranie Ci zdolności do samodzielnego działania. 
              Dezinformacja, propaganda i manipulacja służą temu, by Twoje działania realizowały cudze, nie Twoje cele. 
              <strong style={{ color: 'var(--color-accent)', display: 'block', marginTop: '1rem' }}>
                Kształcimy elity zdolne do samodzielnego zadaniowania się zgodnie z własnym interesem i skutecznego przeciwdziałania utracie tej zdolności.
              </strong>
            </Text>
          </Box>

          <Box className="premium-grid" style={{ marginTop: '3rem' }}>
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="home-feature-title">
                  Rozpoznawanie Własnego Interesu
                </Heading>
                <Text className="home-feature-description" size="3">
                  Ucz się identyfikować swoje rzeczywiste cele i odróżniać je od celów narzucanych przez zewnętrzne wpływy
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="home-feature-title">
                  Skuteczne Zadaniowanie
                </Heading>
                <Text className="home-feature-description" size="3">
                  Rozwijaj zdolność do samodzielnego planowania i realizacji działań zgodnych z Twoim interesem, nie cudzym
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-3">
              <CardContent>
                <Heading as="h3" size="5" className="home-feature-title">
                  Ochrona Autonomii
                </Heading>
                <Text className="home-feature-description" size="3">
                  Przeciwdziałaj próbom ograniczenia Twojej zdolności do samodzielnego myślenia i działania w ramach domeny informacyjnej
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Elite Training Section */}
      <Box as="section" className="home-elite-training">
        <Box className="home-container">
          <Box className="home-elite-content">
            <Heading as="h2" size="8" className="home-section-title-center">
              Dla Kogo Jest Ta Platforma?
            </Heading>
            <Text className="home-elite-text" size="4">
              Kształcimy przyszłe <strong style={{ color: 'var(--color-accent)' }}>elity</strong> - osoby zdolne do samodzielnego zadaniowania się zgodnie z własnym interesem, 
              którzy będą skutecznie realizować swoje cele w środowisku domeny informacyjnej.
            </Text>

            <Box className="home-elite-grid" style={{ marginTop: '3rem' }}>
              <Card size="4">
                <CardContent>
                  <Heading as="h3" size="5" className="home-service-title">
                    Kadra Dowódcza
                  </Heading>
                  <Text className="home-service-text" size="3">
                    Dla liderów zdolnych do samodzielnego planowania i realizacji celów w warunkach wojny informacyjnej
                  </Text>
                </CardContent>
              </Card>

              <Card size="4">
                <CardContent>
                  <Heading as="h3" size="5" className="home-service-title">
                    Decydenci i Analitycy
                  </Heading>
                  <Text className="home-service-text" size="3">
                    Dla osób podejmujących strategiczne decyzje i potrzebujących zdolności do samodzielnej analizy rzeczywistości
                  </Text>
                </CardContent>
              </Card>

              <Card size="4">
                <CardContent>
                  <Heading as="h3" size="5" className="home-service-title">
                    Autonomiczni Obywatele
                  </Heading>
                  <Text className="home-service-text" size="3">
                    Dla każdego, kto chce zachować zdolność do działania zgodnie z własnym interesem, nie cudzym
                  </Text>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* MOCKUP - Main Features Grid - Ukryte do czasu podłączenia prawdziwych danych z DB
      <Box as="section" className="home-features">
        <Box className="home-container">
          <Box className="home-section-header">
            <Text className="home-section-tag" weight="bold" size="1">
              PLATFORMA
            </Text>
            <Heading as="h2" size="8" className="home-section-title">
              Kompleksowa Edukacja
            </Heading>
            <Text className="home-section-subtitle" size="3">
              Rozwijamy krytyczne myślenie systemowe
            </Text>
          </Box>

          <Box className="premium-grid">
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Text className="home-feature-number" weight="bold" size="7">
                  {stats.loading ? '...' : `${stats.modules}${stats.modules > 0 ? '+' : ''}`}
                </Text>
                <Heading as="h3" size="5" className="home-feature-title">
                  Moduły Szkoleniowe
                </Heading>
                <Text className="home-feature-description" size="3">
                  Kompletny program edukacyjny w zakresie cybernetyki i metacybernetyki
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Text className="home-feature-number" weight="bold" size="7">
                  Zadaniowe
                </Text>
                <Heading as="h3" size="5" className="home-feature-title">
                  Praktyczne Szkolenia
                </Heading>
                <Text className="home-feature-description" size="3">
                  Rozwijanie krytycznego myślenia systemowego poprzez praktyczne ćwiczenia
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-3">
              <CardContent>
                <Text className="home-feature-number" weight="bold" size="7">
                  Naukowe
                </Text>
                <Heading as="h3" size="5" className="home-feature-title">
                  Materiały Naukowe
                </Heading>
                <Text className="home-feature-description" size="3">
                  Opracowania oparte na dorobku Józefa Kosseckiego i Mariana Mazura
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
      */}

      {/* Content Lanes (3 ścieżki) */}
      <Box as="section" id="lanes" className="home-services">
        <Box className="home-container">
          <Heading as="h2" size="8" className="home-section-title-center">
            Poznaj i dołącz (materiały + społeczność)
          </Heading>
          
          <Box className="premium-grid">
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Darmowa Cyfrowa Biblioteka
                </Heading>
                <Text className="home-service-text" size="3">
                  Bezpłatny dostęp do zbioru książek i materiałów o cybernetyce, metacybernetyce i krytycznym myśleniu
                </Text>
                <Link to="/biblioteka" className="home-service-link">
                  Przeglądaj Bibliotekę →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Forum Społeczności
                </Heading>
                <Text className="home-service-text" size="3">
                  Wymiana doświadczeń, dyskusje i wsparcie w rozwoju samodzielnego myślenia
                </Text>
                <Link to="/forum" className="home-service-link">
                  Dołącz do Forum →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-3">
              <CardContent>
                <Heading as="h3" size="5" className="home-service-title">
                  Terminy szkoleń
                </Heading>
                <Text className="home-service-text" size="3">
                  Zobacz najbliższe wydarzenia i rezerwacje — wybierz termin, lokalizację i poziom.
                </Text>
                <Link to="/wydarzenia" className="home-service-link">
                  Zobacz Terminy →
                </Link>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Verification Section */}
      <Box as="section" className="home-verification">
        <Box className="home-container">
          <Box className="home-verification-content">
            <Heading as="h2" size="8" className="home-verification-title">
              Potwierdzone i Zweryfikowane
            </Heading>
            <Text className="home-verification-text" size="3">
              Nasza platforma oparta jest na sprawdzonych, naukowych fundamentach.
              Materiały zostały stworzone przez ekspertów w dziedzinie cybernetyki
              i metacybernetyki, zgodnie z najlepszymi praktykami edukacyjnymi.
            </Text>
            <Box className="home-badges">
              <Card size="3" className="home-badge">
                <Text weight="bold" size="2">Naukowe Podstawy</Text>
              </Card>
              <Card size="3" className="home-badge">
                <Text weight="bold" size="2">Zweryfikowane Materiały</Text>
              </Card>
              <Card size="3" className="home-badge">
                <Text weight="bold" size="2">Autorskie Treści</Text>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* MOCKUP - Additional Solutions - Ukryte do czasu podłączenia prawdziwych danych z DB
      <Box as="section" className="home-solutions">
        <Box className="home-container">
          <Box className="home-section-header">
            <Text className="home-section-tag" weight="bold" size="1">
              DODATKOWO
            </Text>
            <Heading as="h2" size="8" className="home-section-title">
              Inne Rozwiązania
            </Heading>
            <Text className="home-section-subtitle" size="3">
              Rozwiązania dla każdego poziomu zaawansowania
            </Text>
          </Box>

          <Box className="premium-grid">
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="home-solution-title">
                  Program Szkoleniowy
                </Heading>
                <Text className="home-solution-text" size="3">
                  Kompleksowy cykl szkoleń rozwijający umiejętności krytycznego myślenia
                </Text>
                <Link to="/szkolenia" className="home-solution-link">
                  Zobacz więcej →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="home-solution-title">
                  Rozwiązania Edukacyjne
                </Heading>
                <Text className="home-solution-text" size="3">
                  Dostosowane materiały edukacyjne dla różnych poziomów zaawansowania
                </Text>
                <Link to="/biblioteka" className="home-solution-link">
                  Zobacz więcej →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-3">
              <CardContent>
                <Heading as="h3" size="5" className="home-solution-title">
                  Materiały Licencyjne
                </Heading>
                <Text className="home-solution-text" size="3">
                  Autorskie treści chronione prawem autorskim
                </Text>
                <Link to="/szkolenia" className="home-solution-link">
                  Zobacz więcej →
                </Link>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
      */}

      {/* CTA */}
      <Box as="section" id="cta" className="home-mission">
        <Box className="home-container">
          <Heading as="h2" size="8" className="home-mission-title">
            Nasza Misja
          </Heading>
          <Text className="home-mission-text" size="4">
            <strong style={{ color: 'var(--color-accent)' }}>Nowoczesna wojna odbywa się w domenie informacyjnej.</strong> 
            {' '}Kształcimy elity zdolne do samodzielnego zadaniowania się zgodnie z własnym interesem. 
            Rozwijamy umiejętność skutecznego działania i przeciwdziałania próbom odebrania tej zdolności przez wrogie wpływy informacyjne.
            Budujemy społeczeństwo osób autonomicznych - działających w oparciu o cybernetyczną analizę rzeczywistości, 
            realizujących własne cele, nie cudze.
          </Text>
          
          <Box style={{ marginTop: '2.5rem' }}>
            <Text size="3" style={{ color: 'var(--text-white-80)' }}>
              Program oparty na dorobku naukowym <strong>Józefa Kosseckiego</strong> i <strong>Mariana Mazura</strong> 
              {' '}– pionierów polskiej cybernetyki i metacybernetyki.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Home

